import { useRef, useState } from "react";
import { Upload } from "lucide-react";

import { cn } from "lib/utils";
import { useLanguage } from "context/languageContext";
import { translations } from "utils/i18n";
import { Button } from "components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/ui/dialog";
import { UploadProgress } from "components/upload-progress";

const MAX_FILE_SIZE_MB = 1024;
const ALL_FORMATS = ["MP3", "WAV", "M4A", "FLAC", "OGG", "MP4", "MOV", "MKV", "WEBM"];

// `source`/`title`/`prompt`/`accept`/`formats` default to the plain audio
// upload flow — the caption tile (see TranscriptionPage) reuses this same
// dialog for video with all five overridden, rather than duplicating the
// drag-and-drop shell for one different file type.
export function UploadDialog({
  open,
  onOpenChange,
  onUpload,
  uploading,
  upload,
  source = "uploaded",
  title,
  prompt,
  accept = "audio/*",
  formats = ALL_FORMATS,
}) {
  const { language } = useLanguage();
  const tr = translations[language].uploadDialog;
  const resolvedTitle = title ?? tr.title;
  const resolvedPrompt = prompt ?? tr.prompt;
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const busy = upload?.busy ?? uploading;
  const inProgress = busy || submitting;

  async function cancelAndClose() {
    await upload?.cancel?.();
    onOpenChange(false);
  }

  async function submit(file) {
    setSubmitting(true);
    try {
      await onUpload(file, source);
    } catch {
      // onUpload owns user-facing failure feedback.
    } finally {
      onOpenChange(false);
      setSubmitting(false);
    }
  }

  function handleFiles(fileList) {
    if (inProgress) return;
    const file = fileList?.[0];
    if (file) void submit(file);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && inProgress) {
          void cancelAndClose();
          return;
        }
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent
        className="sm:max-w-md"
        showCloseButton={!inProgress}
        onEscapeKeyDown={(event) => inProgress && event.preventDefault()}
        onPointerDownOutside={(event) => inProgress && event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{resolvedTitle}</DialogTitle>
        </DialogHeader>

        <div
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            handleFiles(event.dataTransfer.files);
          }}
          className={cn(
            "flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-white/15 px-4 py-5 text-center transition-colors sm:px-6 sm:py-6",
            inProgress ? "min-h-64 sm:min-h-72" : "min-h-56 sm:h-56",
            dragging && "border-white/30 bg-white/5"
          )}
        >
          {inProgress ? (
            <UploadProgress upload={upload} onCancel={cancelAndClose} />
          ) : (
            <>
              <Upload className="size-5 text-muted-foreground" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium text-foreground">{resolvedPrompt}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {tr.maxSizeAndFormats(MAX_FILE_SIZE_MB, formats.join(", "))}
                </p>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()}>
                {tr.chooseFile}
              </Button>
            </>
          )}
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            className="hidden"
            onChange={(event) => {
              handleFiles(event.target.files);
              event.target.value = "";
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
